import {
  ConsultationVideo,
  PaginatedResponse,
  VideoPlaylist,
  VideoPlaylistItem,
  VideoRatingRecord,
} from './types';

export const MAX_VIDEO_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
export const ALLOWED_VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'm4v'];

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export const validateVideoFile = (file: File): { valid: boolean; error?: string } => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_VIDEO_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `Unsupported file type ".${extension}". Allowed: ${ALLOWED_VIDEO_EXTENSIONS.join(', ')}`,
    };
  }
  if (file.size > MAX_VIDEO_SIZE_BYTES) {
    return {
      valid: false,
      error: `File too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(MAX_VIDEO_SIZE_BYTES)}.`,
    };
  }
  return { valid: true };
};

export const getVideoId = (video: ConsultationVideo): string => {
  return video.id || video.video_id || video.consultation_video_id || '';
};

export const getPlaylistId = (playlist: VideoPlaylist): string => {
  return playlist.id || playlist.playlist_id || '';
};

export const getPlaylistItemId = (item: VideoPlaylistItem): string => {
  return item.item_id || item.id || item.playlist_item_id || '';
};

export const getPlaylistItemVideoId = (item: VideoPlaylistItem): string => {
  if (typeof item.video === 'string' && item.video) {
    return item.video;
  }
  if (item.video && typeof item.video === 'object') {
    return getVideoId(item.video);
  }
  return item.video_id || '';
};

export const getPlaylistItemTitle = (item: VideoPlaylistItem): string => {
  const explicitTitle = item.video_title?.trim();
  if (explicitTitle) {
    return explicitTitle;
  }
  if (item.video && typeof item.video === 'object' && item.video.title?.trim()) {
    return item.video.title.trim();
  }
  return 'Untitled video';
};

export const toPlaylistItemVideo = (item: VideoPlaylistItem): ConsultationVideo | null => {
  const videoId = getPlaylistItemVideoId(item);
  if (!videoId) {
    return null;
  }

  const title = getPlaylistItemTitle(item);
  if (item.video && typeof item.video === 'object') {
    return {
      ...item.video,
      video_id: getVideoId(item.video) || videoId,
      title: item.video.title?.trim() || title,
    };
  }

  return {
    video_id: videoId,
    title,
  };
};

export const getRatingId = (rating: VideoRatingRecord): string => {
  return rating.rating_id || rating.id || '';
};

export const nextVideoRatingStats = (
  video: Pick<ConsultationVideo, 'rating_average' | 'rating_count'>,
  savedRating: number,
  previousRating?: number | null
): { rating_average: number; rating_count: number } => {
  const previousCount = video.rating_count ?? 0;
  const parsedAverage = Number(video.rating_average ?? 0);
  const previousAverage = Number.isFinite(parsedAverage) ? parsedAverage : 0;

  if (previousRating) {
    const ratingCount = previousCount > 0 ? previousCount : 1;
    const ratingAverage =
      (previousAverage * ratingCount - previousRating + savedRating) / ratingCount;
    return {
      rating_average: Number(ratingAverage.toFixed(2)),
      rating_count: ratingCount,
    };
  }

  const ratingCount = previousCount + 1;
  const ratingAverage = (previousAverage * previousCount + savedRating) / ratingCount;
  return {
    rating_average: Number(ratingAverage.toFixed(2)),
    rating_count: ratingCount,
  };
};

export const asPaginatedResponse = <T>(
  response: PaginatedResponse<T> | T[] | null | undefined
): PaginatedResponse<T> => {
  if (Array.isArray(response)) {
    return { count: response.length, next: null, previous: null, results: response };
  }

  const results = response?.results ?? [];
  return {
    count: response?.count ?? results.length,
    next: response?.next ?? null,
    previous: response?.previous ?? null,
    results,
  };
};

export const normalizePaginated = <T>(response: PaginatedResponse<T> | T[]): T[] => {
  if (Array.isArray(response)) {
    return response;
  }

  return response.results ?? [];
};

export const formatDuration = (seconds?: number): string => {
  if (!seconds && seconds !== 0) {
    return '';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  const paddedMinutes = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
  const paddedSeconds = String(remainingSeconds).padStart(2, '0');

  return hours > 0
    ? `${hours}:${paddedMinutes}:${paddedSeconds}`
    : `${paddedMinutes}:${paddedSeconds}`;
};

export const formatDate = (value?: string): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};
